import React, { useState, useMemo } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { useAuth } from '../../contexts/AuthContext';
import { useTasks } from '../../hooks/useTasks';
import { useCustomFields } from '../../hooks/useCustomFields';
import { Project, PROJECT_STATUS_OPTIONS, PROJECT_PRIORITY_OPTIONS } from '../../types/project';
import { formatDateTime, formatDate, isOverdue } from '../../utils/dateHelpers';
import ProjectTasksModal from './ProjectTasksModal';
import { 
  PencilSquareIcon, 
  TrashIcon, 
  EyeIcon,
  UserIcon,
  CalendarIcon,
  BuildingOffice2Icon,
  ChartBarIcon,
  CurrencyDollarIcon,
  TagIcon,
  ListBulletIcon
} from '@heroicons/react/24/outline';

// Helper funkció: szöveg rövidítése
const truncateText = (text: string, maxLength: number = 60): string => {
  if (text.length <= maxLength) return text;
  return text.substring(0, maxLength) + '...';
};

interface ProjectListProps {
  projects: Project[];
  loading: boolean;
  onEdit: (project: Project) => void;
  onDelete: (projectId: string) => void;
  onView: (project: Project) => void;
  onUpdateStatus: (projectId: string, status: Project['status']) => void;
  onUpdatePriority: (projectId: string, priority: Project['priority']) => void;
}

const ProjectList: React.FC<ProjectListProps> = ({
  projects,
  loading,
  onEdit,
  onDelete,
  onView,
  onUpdateStatus,
  onUpdatePriority
}) => {
  const { t } = useLanguage();
  const { user } = useAuth();
  const { tasks } = useTasks();
  const { customFields } = useCustomFields('project');
  const [expandedProjects, setExpandedProjects] = useState<Set<string>>(new Set());
  const [tasksModalProject, setTasksModalProject] = useState<Project | null>(null);

  // Projekthez tartozó taskok számának lekérése
  const getTaskCount = (projectId: string) => {
    return tasks.filter(task => task.projectId === projectId).length;
  };

  // Státusz szín lekérése
  const getStatusColor = (status: Project['status']) => {
    const isDark = document.documentElement.classList.contains('dark');
    
    switch (status) {
      case 'planning': 
        return isDark ? '#9ca3af' : '#94a3b8'; // gray
      case 'active': 
        return isDark ? '#60a5fa' : '#2563eb'; // blue
      case 'completed': 
        return isDark ? '#34d399' : '#16a34a'; // green
      case 'paused': 
        return isDark ? '#fbbf24' : '#eab308'; // yellow
      case 'cancelled': 
        return isDark ? '#f87171' : '#dc2626'; // red
      default: 
        return isDark ? '#9ca3af' : '#94a3b8';
    }
  };

  // Prioritás szín lekérése
  const getPriorityColor = (priority: Project['priority']) => {
    const isDark = document.documentElement.classList.contains('dark');
    
    switch (priority) {
      case 'low': 
        return isDark ? '#34d399' : '#22c55e'; // green
      case 'medium': 
        return isDark ? '#fbbf24' : '#eab308'; // yellow
      case 'high': 
        return isDark ? '#fb923c' : '#f97316'; // orange
      case 'urgent': 
        return isDark ? '#f87171' : '#ef4444'; // red
      default: 
        return isDark ? '#34d399' : '#22c55e';
    }
  };

  // Kombinált border stílus
  const getCombinedBorderStyle = (project: Project) => ({
    borderLeftColor: getStatusColor(project.status),
    borderRightColor: getPriorityColor(project.priority)
  });

  // Progressz szín lekérése
  const getProgressColor = (progress: number) => {
    if (progress < 30) return 'bg-red-500';
    if (progress < 70) return 'bg-yellow-500';
    return 'bg-green-500';
  };

  // Expand/collapse kezelés
  const toggleExpand = (projectId: string) => {
    setExpandedProjects(prev => {
      const newSet = new Set(prev);
      if (newSet.has(projectId)) {
        newSet.delete(projectId);
      } else {
        newSet.add(projectId);
      }
      return newSet;
    });
  };

  // Státusz megjelenítés
  const getStatusLabel = (status: Project['status']) => {
    return PROJECT_STATUS_OPTIONS.find(opt => opt.value === status)?.label || status;
  };

  // Prioritás megjelenítés
  const getPriorityLabel = (priority: Project['priority']) => {
    return PROJECT_PRIORITY_OPTIONS.find(opt => opt.value === priority)?.label || priority;
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (projects.length === 0) {
    return (
      <div className="text-center py-12">
        <div className="mx-auto h-12 w-12 text-gray-400 dark:text-gray-500">
          <ChartBarIcon className="h-12 w-12" />
        </div>
        <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-gray-100">
          {t('projects.noProjects') || 'Nincsenek projektek'}
        </h3>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
          {t('projects.noProjectsDescription') || 'Kezdjen el egy új projekt létrehozásával.'}
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Desktop táblázat - 1650px felett */}
      <div className="hidden 2xl:block bg-white dark:bg-gray-800 rounded-lg overflow-hidden shadow-sm border border-gray-200 dark:border-gray-700">
        <div className="overflow-x-auto">
          <table className="w-full divide-y divide-gray-200 dark:divide-gray-700">
          <thead className="bg-gray-50 dark:bg-gray-700">
            <tr>
              <th className="px-2 py-1 text-left text-xs font-medium text-gray-600 dark:text-gray-300 uppercase tracking-tight w-[18%]">
                {t('projects.fields.name') || 'Projekt'}
              </th>
              <th className="px-2 py-1 text-left text-xs font-medium text-gray-600 dark:text-gray-300 uppercase tracking-tight w-[9%]">
                {t('projects.fields.status') || 'Státusz'}
              </th>
              <th className="px-2 py-1 text-left text-xs font-medium text-gray-600 dark:text-gray-300 uppercase tracking-tight w-[9%]">
                {t('projects.fields.priority') || 'Prioritás'}
              </th>
              <th className="px-2 py-1 text-left text-xs font-medium text-gray-600 dark:text-gray-300 uppercase tracking-tight w-[11%]">
                {t('projects.fields.progress') || 'Haladás'}
              </th>
              <th className="px-2 py-1 text-left text-xs font-medium text-gray-600 dark:text-gray-300 uppercase tracking-tight w-[9%]">
                {t('projects.fields.endDate') || 'Határidő'}
              </th>
              <th className="px-2 py-1 text-left text-xs font-medium text-gray-600 dark:text-gray-300 uppercase tracking-tight w-[11%]">
                {t('projects.fields.customer') || 'Ügyfél'}
              </th>
              <th className="px-2 py-1 text-left text-xs font-medium text-gray-600 dark:text-gray-300 uppercase tracking-tight w-[7%]">
                {t('projects.fields.tasks') || 'Feladatok'}
              </th>
              <th className="px-2 py-1 text-left text-xs font-medium text-gray-600 dark:text-gray-300 uppercase tracking-tight w-[9%]">
                {t('projects.fields.budget') || 'Költségvetés'}
              </th>
              <th className="px-2 py-1 text-right text-xs font-medium text-gray-600 dark:text-gray-300 uppercase tracking-tight w-[17%] sticky right-0 bg-gray-50 dark:bg-gray-700 shadow-[-4px_0_6px_-1px_rgba(0,0,0,0.1)]">
                {t('common.actions') || 'Műveletek'}
              </th>
            </tr>
          </thead>
          <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
            {projects.map((project) => (
              <tr
                key={project.id}
                onClick={() => onView(project)}
                className="group hover:bg-gray-50 dark:hover:bg-gray-700 border-l-4 border-r-4 cursor-pointer"
                style={getCombinedBorderStyle(project)}
              >
                <td className="px-2 py-2">
                  <div className="flex flex-col">
                    <div className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                      {project.name}
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">
                      {truncateText(project.description, 60)}
                    </div>
                    {/* Custom Fields */}
                    {customFields.length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-1">
                        {customFields.map(field => {
                          const value = project.customFields?.[field.key];
                          if (value === undefined || value === null || value === '') return null;
                          
                          return (
                            <div key={field.id} className="inline-flex items-center px-2 py-1 rounded-md text-xs bg-gradient-to-r from-purple-50 to-indigo-50 dark:from-purple-900/30 dark:to-indigo-900/30 border border-purple-200 dark:border-purple-700">
                              <span className="font-semibold text-purple-700 dark:text-purple-300">{field.name}:</span>
                              <span className="ml-1 text-gray-700 dark:text-gray-300">
                                {Array.isArray(value) ? (
                                  <span className="inline-flex flex-wrap gap-1 ml-1">
                                    {value.map((val: string, idx: number) => (
                                      <span key={idx} className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-purple-100 dark:bg-purple-800/50 text-purple-800 dark:text-purple-200">
                                        {val}
                                      </span>
                                    ))}
                                  </span>
                                ) : field.type === 'checkbox' ? (
                                  <span className="font-medium">{value ? '✓' : '✗'}</span>
                                ) : field.type === 'date' ? (
                                  <span>{new Date(value).toLocaleDateString('hu-HU')}</span>
                                ) : (
                                  <span>{String(value)}</span>
                                )}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </td>
                <td className="px-2 py-2">
                  <select
                    value={project.status}
                    onChange={(e) => onUpdateStatus(project.id, e.target.value as Project['status'])}
                    onClick={(e) => e.stopPropagation()}
                    className="text-xs font-medium rounded-full px-1.5 py-0.5 focus:outline-none focus:ring-1 focus:ring-primary-500 border-0"
                    style={{
                      backgroundColor: getStatusColor(project.status) + (document.documentElement.classList.contains('dark') ? '40' : '30'),
                      color: getStatusColor(project.status)
                    }}
                  >
                    {PROJECT_STATUS_OPTIONS.map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </td>
                <td className="px-2 py-2">
                  <select
                    value={project.priority}
                    onChange={(e) => onUpdatePriority(project.id, e.target.value as Project['priority'])}
                    onClick={(e) => e.stopPropagation()}
                    className="text-xs font-medium rounded-full px-1.5 py-0.5 focus:outline-none focus:ring-1 focus:ring-primary-500 border-0"
                    style={{
                      backgroundColor: getPriorityColor(project.priority) + (document.documentElement.classList.contains('dark') ? '40' : '30'),
                      color: getPriorityColor(project.priority)
                    }}
                  >
                    {PROJECT_PRIORITY_OPTIONS.map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </td>
                <td className="px-2 py-2">
                  <div className="flex items-center space-x-1">
                    <div className="flex-1 bg-gray-200 dark:bg-gray-600 rounded-full h-2">
                      <div
                        className={`h-2 rounded-full ${getProgressColor(project.progress)}`}
                        style={{ width: `${project.progress}%` }}
                      />
                    </div>
                    <span className="text-xs font-medium text-gray-900 dark:text-gray-100 w-8 text-right">
                      {project.progress}%
                    </span>
                  </div>
                </td>
                <td className="px-2 py-2">
                  <div className="text-xs text-gray-900 dark:text-gray-100">
                    {formatDate(project.endDate)}
                  </div>
                  {isOverdue(project.endDate) && project.status !== 'completed' && (
                    <div className="text-xs text-red-600 dark:text-red-400 font-medium">
                      {t('projects.overdue') || 'Lejárt'}
                    </div>
                  )}
                </td>
                <td className="px-2 py-2">
                  <div className="text-xs text-gray-900 dark:text-gray-100 truncate">
                    {project.customerName || '-'}
                  </div>
                </td>
                <td className="px-2 py-2">
                  <div className="flex items-center space-x-1">
                    <ListBulletIcon className="h-3 w-3 text-teal-600 dark:text-teal-400" />
                    <span className="text-xs text-gray-900 dark:text-gray-100">
                      {getTaskCount(project.id)}
                    </span>
                  </div>
                </td>
                <td className="px-2 py-2">
                  <div className="text-xs text-gray-900 dark:text-gray-100">
                    {project.budget ? `${project.budget.toLocaleString()} Ft` : '-'}
                  </div>
                </td>
                <td className="px-2 py-1 sticky right-0 bg-white dark:bg-gray-800 group-hover:bg-gray-50 dark:group-hover:bg-gray-700 shadow-[-4px_0_6px_-1px_rgba(0,0,0,0.1)]">
                  <div className="flex items-center justify-end space-x-1">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onView(project);
                      }}
                      className="text-blue-600 dark:text-blue-400 hover:text-blue-900 dark:hover:text-blue-300 p-1.5 rounded hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors"
                      title={t('common.view') || 'Megtekintés'}
                    >
                      <EyeIcon className="h-4 w-4" />
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setTasksModalProject(project);
                      }}
                      className="text-teal-600 dark:text-teal-400 hover:text-teal-900 dark:hover:text-teal-300 p-1.5 rounded hover:bg-teal-50 dark:hover:bg-teal-900/20 transition-colors"
                      title={t('projects.actions.manageTasks') || 'Feladatok kezelése'}
                    >
                      <ListBulletIcon className="h-4 w-4" />
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onEdit(project);
                      }}
                      className="text-indigo-600 dark:text-indigo-400 hover:text-indigo-900 dark:hover:text-indigo-300 p-1.5 rounded hover:bg-indigo-50 dark:hover:bg-indigo-900/20 transition-colors"
                      title={t('common.edit') || 'Szerkesztés'}
                    >
                      <PencilSquareIcon className="h-4 w-4" />
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onDelete(project.id);
                      }}
                      className="text-red-600 dark:text-red-400 hover:text-red-900 dark:hover:text-red-300 p-1.5 rounded hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                      title={t('common.delete') || 'Törlés'}
                    >
                      <TrashIcon className="h-4 w-4" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      </div>

      {/* Mobil kártyák - 1650px alatt */}
      <div className="2xl:hidden space-y-4">
        {projects.map((project) => (
          <div
            key={project.id}
            className="bg-white dark:bg-gray-800 rounded-lg p-3 shadow-md border border-gray-200 dark:border-gray-700 border-l-4 border-r-4"
            style={getCombinedBorderStyle(project)}
          >
            {/* Projekt fejléc */}
            <div className="flex items-start justify-between mb-3">
              <div className="flex-1 min-w-0">
                <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                  {project.name}
                </h3>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  {truncateText(project.description, 50)}
                </p>
              </div>
              <div className="flex space-x-1 ml-2">
                <button
                  onClick={() => onView(project)}
                  className="text-blue-600 dark:text-blue-400 hover:text-blue-900 dark:hover:text-blue-300 p-1 rounded hover:bg-blue-50 dark:hover:bg-blue-900/20"
                >
                  <EyeIcon className="h-4 w-4" />
                </button>
                <button
                  onClick={() => setTasksModalProject(project)}
                  className="text-teal-600 dark:text-teal-400 hover:text-teal-900 dark:hover:text-teal-300 p-1 rounded hover:bg-teal-50 dark:hover:bg-teal-900/20"
                >
                  <ListBulletIcon className="h-4 w-4" />
                </button>
                <button
                  onClick={() => onEdit(project)}
                  className="text-indigo-600 dark:text-indigo-400 hover:text-indigo-900 dark:hover:text-indigo-300 p-1 rounded hover:bg-indigo-50 dark:hover:bg-indigo-900/20"
                >
                  <PencilSquareIcon className="h-4 w-4" />
                </button>
                <button
                  onClick={() => onDelete(project.id)}
                  className="text-red-600 dark:text-red-400 hover:text-red-900 dark:hover:text-red-300 p-1 rounded hover:bg-red-50 dark:hover:bg-red-900/20"
                >
                  <TrashIcon className="h-4 w-4" />
                </button>
              </div>
            </div>

            {/* Státusz és prioritás */}
            <div className="grid grid-cols-2 gap-2 mb-3">
              <div>
                <label className="text-xs text-gray-500 dark:text-gray-400 mb-1 block">
                  {t('projects.fields.status') || 'Státusz'}
                </label>
                <select
                  value={project.status}
                  onChange={(e) => onUpdateStatus(project.id, e.target.value as Project['status'])}
                  className="text-xs font-medium rounded-full px-1.5 py-0.5 w-full focus:outline-none focus:ring-1 focus:ring-primary-500 border-0"
                  style={{
                    backgroundColor: getStatusColor(project.status) + (document.documentElement.classList.contains('dark') ? '40' : '30'),
                    color: getStatusColor(project.status)
                  }}
                >
                  {PROJECT_STATUS_OPTIONS.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-xs text-gray-500 dark:text-gray-400 mb-1 block">
                  {t('projects.fields.priority') || 'Prioritás'}
                </label>
                <select
                  value={project.priority}
                  onChange={(e) => onUpdatePriority(project.id, e.target.value as Project['priority'])}
                  className="text-xs font-medium rounded-full px-1.5 py-0.5 w-full focus:outline-none focus:ring-1 focus:ring-primary-500 border-0"
                  style={{
                    backgroundColor: getPriorityColor(project.priority) + (document.documentElement.classList.contains('dark') ? '40' : '30'),
                    color: getPriorityColor(project.priority)
                  }}
                >
                  {PROJECT_PRIORITY_OPTIONS.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Haladás */}
            <div className="mb-3">
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs text-gray-500">
                  {t('projects.fields.progress') || 'Haladás'}
                </span>
                <span className="text-xs font-medium text-gray-900">
                  {project.progress}%
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className={`h-2 rounded-full ${getProgressColor(project.progress)}`}
                  style={{ width: `${project.progress}%` }}
                />
              </div>
            </div>

            {/* További részletek */}
            <div className="space-y-2">
              <div className="flex items-center space-x-2">
                <CalendarIcon className="h-4 w-4 text-gray-400" />
                <div className="flex-1 text-xs text-gray-600">
                  {formatDate(project.endDate)}
                  {isOverdue(project.endDate) && project.status !== 'completed' && (
                    <span className="text-red-600 font-medium ml-1">
                      ({t('projects.overdue') || 'Lejárt'})
                    </span>
                  )}
                </div>
              </div>

              {project.customerName && (
                <div className="flex items-center space-x-2">
                  <BuildingOffice2Icon className="h-4 w-4 text-gray-400" />
                  <div className="flex-1 text-xs text-gray-600">
                    {project.customerName}
                  </div>
                </div>
              )}

              {project.budget && (
                <div className="flex items-center space-x-2">
                  <CurrencyDollarIcon className="h-4 w-4 text-gray-400" />
                  <div className="flex-1 text-xs text-gray-600">
                    {project.budget.toLocaleString()} Ft
                  </div>
                </div>
              )}

              {project.assignedUsersNames && project.assignedUsersNames.length > 0 && (
                <div className="flex items-center space-x-2">
                  <UserIcon className="h-4 w-4 text-gray-400" />
                  <div className="flex-1 text-xs text-gray-600">
                    {project.assignedUsersNames.join(', ')}
                  </div>
                </div>
              )}

              <div className="flex items-center space-x-2">
                <ListBulletIcon className="h-4 w-4 text-teal-600" />
                <div className="flex-1 text-xs text-gray-600">
                  {getTaskCount(project.id)} {t('projects.fields.tasks') || 'feladat'}
                </div>
              </div>

              {project.tags.length > 0 && (
                <div className="flex items-center space-x-2">
                  <TagIcon className="h-4 w-4 text-gray-400" />
                  <div className="flex-1 flex flex-wrap gap-1">
                    {project.tags.map(tag => (
                      <span
                        key={tag}
                        className="inline-block px-2 py-1 text-xs bg-gray-100 text-gray-700 rounded-full"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Project Tasks Modal */}
      {tasksModalProject && (
        <ProjectTasksModal
          project={tasksModalProject}
          isOpen={!!tasksModalProject}
          onClose={() => setTasksModalProject(null)}
        />
      )}
    </div>
  );
};

export default ProjectList; 